import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Button from 'component/atom/forms/button/Button';
import Header from 'component/layout/header/Header';
import Card from 'component/atom/card/Card';
import Input from 'component/atom/forms/input/input/Input';
import RoundedTabs from 'component/atom/Tabs/RoundedTabs';
import { useFetch } from 'hooks/useFetch';
import { groupEndpoints } from 'hooks/endpoints';
import styles from './NewGroup.module.scss';
import { usePost } from 'hooks/usePost';
import UserItem from 'component/molecules/users/groups/components/UserItem/UserItem';
import UploadModal from 'component/hoc/upload-modal/UploadModal';
import Modal from 'component/hoc/modal/Modal';
import toast from 'react-hot-toast';
import { groupsToast } from 'helpers/toastMessages';

function NewGroup() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [tagName, setTagName] = useState('');
  const [searchText, setSearchText] = useState('');
  const [activeUsersTab, setActiveUsersTab] = useState(0);
  const [activeMembersTab, setActiveMembersTab] = useState(0);
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const { data: group, refetch: refetchGroup } = useFetch(
    ['group', id],
    groupEndpoints.getGroupById(id),
    {
      enabled: false,
    }
  );

  const handleDownloadTemplate = () => {
    const filePath = '/documents/SampleGroupUsers.xlsx';
    const a = document.createElement('a');
    a.href = process.env.PUBLIC_URL + filePath;
    a.download = 'SampleGroupUsers.xlsx';

    a.click();
  };

  const handleSelectMember = (userId) => {
    if (selectedMembers.includes(userId)) {
      setSelectedMembers(selectedMembers.filter((id) => id !== userId));
    } else {
      setSelectedMembers([...selectedMembers, userId]);
    }
  };

  const { data: groupImage, refetch: refetchGroupImage } = useFetch(
    ['groupImage', id],
    groupEndpoints.getGroupImage(id),
    {
      enabled: false,
    }
  );

  const [filteredUsers, setFilteredUsers] = useState(
    0 === activeMembersTab
      ? group?.data[0]?.groupMemberUserResponse
      : group?.data[0]?.groupAdminUserResponse
  );

  useEffect(() => {
    if (searchText != '') {
      var filtered = group?.data[0]?.groupMemberUserResponse.filter((c) =>
        c.userName.includes(searchText)
      );

      var filteredAdmin = group?.data[0]?.groupAdminUserResponse.filter((c) =>
        c.userName.includes(searchText)
      );
      setFilteredUsers(0 === activeMembersTab ? filtered : filteredAdmin);
    } else {
      setFilteredUsers(
        0 === activeMembersTab
          ? group?.data[0]?.groupMemberUserResponse
          : group?.data[0]?.groupAdminUserResponse
      );
    }
  }, [searchText, activeMembersTab]);

  useEffect(() => {
    setFilteredUsers(
      0 === activeMembersTab
        ? group?.data[0]?.groupMemberUserResponse
        : group?.data[0]?.groupAdminUserResponse
    );
  }, [group, activeMembersTab]);

  const { data: users, refetch: refetchUsers } = useFetch(
    ['usersGroup', id],
    groupEndpoints.getUserNotInGroup(id),
    {
      enabled: false,
    }
  );

  const { mutate: AddGroup } = usePost({
    onSuccessToast: groupsToast.create,
    toastType: 'add',
  });

  const { mutate: editGroupName } = usePost({
    onSuccessToast: groupsToast.update,
    toastType: 'update',
  });

  const { mutate: bulkAddUsers } = usePost({
    onSuccessToast: groupsToast.addUserToGroup,
    toastType: 'add',
  });

  const { mutate: deleteMember } = usePost({
    onSuccessToast: groupsToast.removeMember,
    toastType: 'update',
  });

  const { mutate: deleteTag } = usePost({
    onSuccess: () => refetchGroup(),
  });

  const { mutate: addTag } = usePost({
    onSuccess: () => {
      setTagName('');
      refetchGroup();
    },
  });

  const { mutate: uploadImage } = usePost({
    onSuccess: () => {
      refetchGroupImage();
    },
  });

  const { mutate: uploadFiles } = usePost({
    onSuccessToast: groupsToast.bulkAddUsers,
    toastType: 'update',
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    const maxSizeInBytes = 500 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error(
        'The selected image exceeds the maximum size of 500KB. Please upload a smaller file.'
      );
      return;
    }

    const formData = new FormData();
    formData.append('FileToUpload', file);
    formData.append('GroupId', group?.data[0]?.groupId);

    uploadImage({
      url: groupEndpoints.uploadImage(),
      data: formData,
      requireAuth: true,
    });
  };

  const handleUploadFiles = () => {
    selectedFiles.map((file, index) => {
      const formData = new FormData();
      formData.append('FileToUpload', file);
      formData.append('GroupId', group?.data[0]?.groupId);
      uploadFiles(
        {
          url: groupEndpoints.bulkUploadGroupUsers(),
          data: formData,
          requireAuth: true,
        },
        {
          onSuccess: () => {
            refetchGroup();
            if (index === selectedFiles.length - 1) {
              setShowUploadModal(false);
            }
          },
        }
      );
    });
  };

  useEffect(() => {
    if (id) {
      refetchUsers();
      refetchGroup();
      refetchGroupImage();
    }
  }, [id]);

  const actions = [
    {
      type: 'button',
      label: 'Add',
      func: () => setShowAddUsersModal(true),
    },
    {
      type: 'button',
      label: 'Upload CSV',
      func: () => setShowUploadModal(true),
    },
  ];

  const validationSchema = Yup.object().shape({
    groupName: Yup.string()
      .required('Group Name is required')
      .min(3, 'Group Name must be at least 3 characters'),
  });

  const renderMembers = () => {
    return (
      <div className={styles['members-list']}>
        {filteredUsers &&
          filteredUsers.map((user) => (
            <UserItem
              key={user?.userId}
              user={user}
              type='member'
              handleDelete={(userId) =>
                deleteMember(
                  {
                    url: groupEndpoints.removeUser(),
                    data: {
                      groupId: group?.data[0]?.groupId,
                      userId: userId,
                    },
                    requireAuth: true,
                  },
                  {
                    onSuccess: () => {
                      refetchUsers();
                      refetchGroup();
                    },
                  }
                )
              }
            />
          ))}
      </div>
    );
  };

  const renderUsers = () => {
    return (
      <div className={styles['members-list']}>
        {users &&
          users?.data?.result.map((user) => (
            <UserItem
              key={user?.userId}
              user={user}
              type='user'
              isSelected={selectedMembers.includes(user.userId)}
              onSelect={(userId) => handleSelectMember(userId)}
            />
          ))}
      </div>
    );
  };

  const tabs = [
    { label: 'Member', content: renderMembers() },
    { label: 'Admin', content: renderMembers() },
  ];

  const usersTabs = [
    { label: 'Member', content: renderUsers() },
    { label: 'Admin', content: renderUsers() },
  ];

  const newGroupBody = (formik) => {
    const { errors, touched, handleChange, handleBlur, values } = formik;

    return (
      <div className={styles['new-group-body']}>
        <div className={styles['upload-image-container']}>
          {groupImage?.data?.result?.imageBytes ? (
            <img
              src={`data:image/jpeg;base64,${groupImage?.data?.result?.imageBytes}`}
            />
          ) : (
            <img src='/images/group-empty.png' alt='Group Placeholder' />
          )}
          {id && (
            <span onClick={() => (id ? fileInputRef.current.click() : {})}>
              Edit Photo
            </span>
          )}
          <input
            id='file-upload'
            type='file'
            accept='image/*'
            onChange={handleImageChange}
            ref={fileInputRef}
            hidden
          />
        </div>
        <Input
          label='Group Name'
          name='groupName'
          placeholder='Enter group name'
          value={values.groupName}
          onChange={handleChange}
          onBlur={handleBlur}
          error={errors.groupName}
          touched={touched.groupName}
        />
      </div>
    );
  };

  const addTagBody = () => {
    return (
      <div className={styles['add-tag']}>
        <div className={styles['add-tag-body']}>
          <Input value={tagName} onChange={(e) => setTagName(e.target.value)} />
          <Button
            label='Add Tag'
            onClick={() =>
              tagName
                ? addTag({
                    url: groupEndpoints.addTag(),
                    data: {
                      tag: tagName,
                      groupId: group?.data[0]?.groupId,
                    },
                    requireAuth: true,
                  })
                : {}
            }
            buttonType='button'
          />
        </div>
        <div className={styles['tags-wrapper']}>
          {group?.data[0]?.groupTagResponse &&
            group?.data[0].groupTagResponse.map((tag) => (
              <div key={tag.tagId} className={styles['tag-item']}>
                <span>{tag.tagName}</span>
                <img
                  src='/images/remove.svg'
                  onClick={() =>
                    deleteTag({
                      url: groupEndpoints.deleteTag(),
                      data: {
                        groupTagId: tag.groupTagId,
                      },
                      requireAuth: true,
                    })
                  }
                />
              </div>
            ))}
        </div>
      </div>
    );
  };

  const usersBody = () => {
    return (
      <div className={styles['users-body']}>
        <Input
          type='/images/search.svg'
          placeholder='Search users'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <RoundedTabs
          tabs={tabs}
          width='100%'
          activeTab={activeMembersTab}
          setActiveTab={setActiveMembersTab}
        />
      </div>
    );
  };

  const initialvalues = {
    groupName: group?.data[0].groupName || '',
  };

  return (
    <Formik
      initialValues={initialvalues}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={() => {}}
    >
      {(formik) => (
        <Form>
          <div className={styles['page-wrapper']}>
            <Header
              isBack
              title={id ? 'Update Group' : 'Create New Group'}
              children={
                <div className={styles['header-content']}>
                  <Button
                    label='Save'
                    buttonType='button'
                    type='save'
                    isDisabled={!formik.values.groupName}
                    onClick={() => {
                      const values = formik.values;
                      id
                        ? editGroupName(
                            {
                              url: groupEndpoints?.editGroupName(),
                              data: {
                                groupId: group?.data[0]?.groupId,
                                groupName: values.groupName,
                              },
                            },
                            {
                              onSuccess: () => {
                                navigate(-1);
                              },
                            }
                          )
                        : AddGroup(
                            {
                              url: groupEndpoints?.createGroup(),
                              data: { groupName: values.groupName },
                            },
                            {
                              onSuccess: () => {
                                navigate(-1);
                              },
                            }
                          );
                    }}
                  />
                </div>
              }
            />
            <div className={styles['new-group-container']}>
              <div className={styles['new-group-container-col']}>
                <Card body={newGroupBody(formik)} />
                {id && <Card title='Add Tag' body={addTagBody()} />}
              </div>
              <div className={styles['new-group-container-col']}>
                {id && (
                  <Card
                    title='Add Users'
                    body={usersBody()}
                    actions={actions}
                  />
                )}
              </div>
            </div>
          </div>
          <UploadModal
            title='Upload File'
            isOpen={showUploadModal}
            onClose={() => setShowUploadModal(false)}
            setSelectedFiles={setSelectedFiles}
            handleSubmit={() => handleUploadFiles()}
            footer={
              <div>
                <span
                  className={styles['download-template']}
                  onClick={handleDownloadTemplate}
                >
                  Download CSV Template
                </span>
              </div>
            }
          />
          <Modal
            title='Add User'
            isOpen={showAddUsersModal}
            onClose={() => {
              refetchUsers();
              setShowAddUsersModal(!showAddUsersModal);
              setSelectedMembers([]);
            }}
            modalContent={{
              component: (
                <div>
                  <RoundedTabs
                    tabs={usersTabs}
                    width='100%'
                    activeTab={activeUsersTab}
                    setActiveTab={setActiveUsersTab}
                  />
                </div>
              ),
            }}
            onSubmit={() =>
              bulkAddUsers(
                {
                  url: groupEndpoints?.bulkAddUsers(),
                  data: {
                    userIds: selectedMembers,
                    isAdmin: 0 === activeUsersTab ? false : true,
                    groupId: group?.data[0]?.groupId,
                  },
                },
                {
                  onSuccess: () => {
                    refetchGroup();
                    setShowAddUsersModal(false);
                    refetchUsers();
                    setSelectedMembers([]);
                  },
                }
              )
            }
            buttonType='button'
            onSubmitstyles={{
              onSubmit: {
                label: 'Submit',
                opicBAckground: true,
              },
            }}
          />
        </Form>
      )}
    </Formik>
  );
}

export default NewGroup;
